import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

export default function DurationConvexityCalculator() {
  const [faceValue, setFaceValue] = useState("");
  const [couponRate, setCouponRate] = useState("");
  const [ytm, setYtm] = useState("");
  const [yearsToMaturity, setYearsToMaturity] = useState("");
  const [result, setResult] = useState<{ duration: number; convexity: number } | null>(null);

  const calculate = () => {
    const face = parseFloat(faceValue);
    const coupon = parseFloat(couponRate) / 100;
    const yieldRate = parseFloat(ytm) / 100;
    const years = parseFloat(yearsToMaturity);

    if (isNaN(face) || isNaN(coupon) || isNaN(yieldRate) || isNaN(years) || 
        face <= 0 || years <= 0) {
      toast.error("Please enter valid values");
      return;
    }

    const annualCoupon = face * coupon;
    let price = 0;
    let weightedTime = 0;
    let convexitySum = 0;

    // Calculate bond price and weighted time
    for (let t = 1; t <= years; t++) {
      const pv = annualCoupon / Math.pow(1 + yieldRate, t);
      price += pv;
      weightedTime += t * pv;
      convexitySum += (t * (t + 1) * pv) / Math.pow(1 + yieldRate, 2);
    }

    // Add face value payment at maturity
    const faceValuePV = face / Math.pow(1 + yieldRate, years);
    price += faceValuePV;
    weightedTime += years * faceValuePV;
    convexitySum += (years * (years + 1) * faceValuePV) / Math.pow(1 + yieldRate, 2);

    // Macaulay Duration
    const duration = weightedTime / price;
    
    // Convexity
    const convexity = convexitySum / price;

    setResult({ duration, convexity });
    toast.success("Duration and Convexity calculated!");
  };

  const reset = () => {
    setFaceValue("");
    setCouponRate("");
    setYtm("");
    setYearsToMaturity("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Duration & Convexity Calculator</CardTitle>
          <CardDescription>
            Measure bond price sensitivity to interest rate changes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="face">Face Value ($)</Label>
            <Input
              id="face"
              type="number"
              placeholder="1000"
              value={faceValue}
              onChange={(e) => setFaceValue(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="coupon">Annual Coupon Rate (%)</Label>
            <Input
              id="coupon"
              type="number"
              placeholder="5"
              value={couponRate}
              onChange={(e) => setCouponRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ytm">Yield to Maturity (%)</Label>
            <Input
              id="ytm"
              type="number"
              placeholder="6"
              value={ytm}
              onChange={(e) => setYtm(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="years">Years to Maturity</Label>
            <Input
              id="years"
              type="number"
              placeholder="10"
              value={yearsToMaturity}
              onChange={(e) => setYearsToMaturity(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 space-y-3">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Macaulay Duration:</strong> {result.duration.toFixed(3)} years
                  </p>
                  <CopyResultButton value={result.duration.toFixed(3)} />
                </div>
              </div>
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg">
                    <strong>Convexity:</strong> {result.convexity.toFixed(3)}
                  </p>
                  <CopyResultButton value={result.convexity.toFixed(3)} />
                </div>
              </div>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["duration-convexity"]} trigger={result?.duration} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
